import type { EstimatedTimeName } from '@gat-ship/types'
import type {
  AddEstimatedTimeNamePayload,
  UpdateEstimatedTimeNamePayload
} from '@gat-ship/types/injection-keys'
import { mockEstimatedTimeNames } from '../model/mock-estimated-time-names'

/**
 * Stubbed network layer for the estimated-time-names registry.
 *
 * The real backend isn't ready yet; these helpers fake a request with
 * a small delay against an in-memory copy of `mockEstimatedTimeNames`
 * so the UI exercises the same shape it'll have once
 * `useGsCloudBackend()` calls replace the body of each function.
 *
 * TEMPORARY: the store is persisted to localStorage so edits survive a
 * page refresh while prototyping. Remove the localStorage layer
 * (STORAGE_KEY, loadStore, persistStore) together with the rest of this
 * mock when the real backend endpoints land — the server is the store.
 */

const SIMULATED_LATENCY_MS = 120

const STORAGE_KEY = 'gs-cloud.mock.estimated-time-names'

function loadStore(): EstimatedTimeName[] {
  // localStorage is absent in unit tests (node environment).
  if (typeof localStorage === 'undefined') {
    return mockEstimatedTimeNames.map((e) => ({ ...e }))
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed as EstimatedTimeName[]
    }
  } catch {
    // Corrupt entry — fall back to the seed below.
  }
  return mockEstimatedTimeNames.map((e) => ({ ...e }))
}

function persistStore() {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
}

let store: EstimatedTimeName[] = loadStore()

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) =>
    setTimeout(() => resolve(value), SIMULATED_LATENCY_MS)
  )
}

function clone(item: EstimatedTimeName): EstimatedTimeName {
  return { ...item }
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `mock-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export async function getEstimatedTimeNamesRequest(): Promise<
  EstimatedTimeName[]
> {
  return delay(store.map(clone))
}

export async function addEstimatedTimeNameRequest(
  payload: AddEstimatedTimeNamePayload
): Promise<EstimatedTimeName> {
  const next: EstimatedTimeName = {
    id: uuid(),
    code: payload.code,
    name: payload.name,
    referenceTime: payload.referenceTime,
    isInactive: payload.isInactive ?? false
  }
  store = [...store, next]
  persistStore()
  return delay(clone(next))
}

export async function updateEstimatedTimeNameRequest(
  id: string,
  payload: UpdateEstimatedTimeNamePayload
): Promise<EstimatedTimeName> {
  const existing = store.find((e) => e.id === id)
  if (!existing) throw new Error(`EstimatedTimeName ${id} not found`)
  const merged: EstimatedTimeName = {
    id: existing.id,
    code: payload.code ?? existing.code,
    name: payload.name ?? existing.name,
    referenceTime: payload.referenceTime ?? existing.referenceTime,
    isInactive: payload.isInactive ?? existing.isInactive
  }
  store = store.map((e) => (e.id === id ? merged : e))
  persistStore()
  return delay(clone(merged))
}

export async function removeEstimatedTimeNameRequest(
  id: string
): Promise<void> {
  store = store.filter((e) => e.id !== id)
  persistStore()
  return delay(undefined)
}
